import { AuthApiErrorCode } from '@mfw/common';

import BaseApiResponseError from './BaseApiResponseError';

/**
 * WHY the login page is being shown, when the app can say so truthfully.
 *
 * One value today, and a union rather than a boolean because the next one that earns a
 * sentence of its own (an operator-disabled account, say) must not have to rewrite every
 * caller. `'credentials-changed'` is the INTERNAL name for the one thing the two codes
 * below actually support: the credential this tab was holding is no longer good, either
 * because it expired or because it was replaced. It is deliberately not called
 * "password changed" — see `getSignOutReason`.
 */
export type SignOutReason = 'credentials-changed';

const STORAGE_KEY = 'mfw.signOutReason';

const REASONS: readonly SignOutReason[] = ['credentials-changed'];

const isSignOutReason = (value: unknown): value is SignOutReason =>
  REASONS.some((reason) => reason === value);

/**
 * WHICH sign-out may be described, and it is NOT the same question as "is this session
 * over".
 *
 * `isRefusedRenewalError` answers the second one and must keep answering it for five
 * codes; this answers the first for two. The three it drops each end the session without
 * saying anything about a credential: `TENANT_MISMATCH` is a verified identity the guard
 * will not bind to an account here, `ACCOUNT_SUSPENDED` is an archived or deleted account,
 * and `auth/user-disabled` is an account an operator switched off. Telling any of those
 * three users that their "credentials have changed" sends them to a password reset that
 * cannot help. A bare 401, a connectivity drop and `SessionMovedError` are dropped for the
 * same reason, one step further out: none of them is even evidence of a decision.
 *
 * What is left supports expiry OR replacement, and only that — which is exactly what the
 * copy under `auth.loginPage.signedOutCredentialsChanged` says. `SESSION_EXPIRED` is the
 * backend refusing a token minted before the account's revocation cutoff, and
 * `auth/user-token-expired` is Google refusing the refresh token the SDK still held. A
 * password change produces both; so does an administrator revoking sessions, and so does
 * a refresh token that simply aged out. Naming a password is more than either code knows.
 */
export function getSignOutReason(error: unknown): SignOutReason | null {
  if (error instanceof BaseApiResponseError) {
    return error.code === AuthApiErrorCode.SESSION_EXPIRED
      ? 'credentials-changed'
      : null;
  }
  if (typeof error === 'object' && error !== null && 'code' in error) {
    return (error as { code?: unknown }).code === 'auth/user-token-expired'
      ? 'credentials-changed'
      : null;
  }
  return null;
}

/**
 * The note, kept per TAB.
 *
 * `sessionStorage` rather than `localStorage`: the sign-out that produced it happened in
 * THIS tab, and a second tab of the same browser — possibly signed in as somebody else —
 * has no business rendering an alert about it. It has to outlive a document, though,
 * because the whole delivery problem is that the refusal and the login page it explains
 * are separated by a navigation the provider performs.
 *
 * Every access is wrapped: a private window, blocked site data, or a browser that throws
 * on the very property read all end here, and the notice is worth far less than the page
 * it sits on. `memoryReason` is the bounded fallback — ONE slot, never a queue, because a
 * second unconsumed note is by definition about a session that is already gone.
 *
 * Nothing but the value above is ever written: no token, no account, no error text.
 */
let memoryReason: SignOutReason | null = null;

/**
 * WHICH terminal incident owns the slot.
 *
 * A sign-out is observed by up to three callbacks at once (the socket hook, an HTTP cache
 * subscriber, and the Firebase SDK's own null callback), and the token read that CAUSED it
 * may only reject after the login page has already mounted. So "one notice per incident"
 * cannot be a flag on the writer or a debounce on the clock; it is ownership. `scope` is
 * the incident, `notedScope` is the incident that has already spoken, and neither is moved
 * by consuming the note — which is what keeps a straggler from re-creating a notice the
 * page has just shown and dismissed.
 */
let scope = 0;
let notedScope: number | null = null;

const listeners = new Set<() => void>();

function readStored(): SignOutReason | null {
  let stored: string | null;
  try {
    stored = window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return memoryReason;
  }
  if (stored === null) return memoryReason;
  // Anything else in the key is not ours — an older build's value, or something that
  // shares the tab. It is dropped rather than shown.
  return isSignOutReason(stored) ? stored : null;
}

/**
 * Advances the incident, so the NEXT terminal failure may speak again.
 *
 * Called at the boundaries that make older identity work obsolete: an explicit sign-in or
 * credential transition starting, the manual logout, a new non-null identity or acting
 * tenant being published, and the provider's own disposal. Deliberately NOT called when
 * the ended identity is being cleaned up — the no-user branch and `clearLocalSession` are
 * the SAME incident as the refusal that caused them, and advancing there would hand the
 * slot to the cleanup and silence the very note the cleanup is the consequence of.
 */
export function beginSignOutReasonScope() {
  scope += 1;
  notedScope = null;
}

/** Drops a note nobody has read yet, leaving incident ownership exactly where it is. */
export function clearSignOutReason() {
  memoryReason = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // nothing to do: the in-memory slot above is the one that mattered
  }
}

/**
 * Records the reason for the sign-out this incident is performing, if it has not already.
 *
 * Synchronous notification, because the login page may already be mounted and subscribed:
 * the Firebase SDK signs out locally BEFORE the refused read rejects, so the navigation
 * routinely happens first.
 */
export function noteSignOutReason(reason: SignOutReason) {
  if (notedScope === scope) return;
  notedScope = scope;
  memoryReason = reason;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, reason);
  } catch {
    // nothing to do: `memoryReason` carries it for the life of this document
  }
  // A copy, so a listener unsubscribing from inside its own callback — which is exactly
  // what the login page's one-shot consumption does — cannot disturb this iteration.
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch {
      // A consumer that throws is its own problem; the remaining ones still hear.
    }
  }
}

/** Reads the note and consumes it. A second reader of the same incident gets nothing. */
export function takeSignOutReason(): SignOutReason | null {
  const reason = readStored();
  clearSignOutReason();
  return reason;
}

export function subscribeSignOutReason(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The ONE place a session token read is classified.
 *
 * Every reader of a Firebase ID token in this app goes through here — the session guard's
 * `getIdToken` dependency, the provider's forced refresh, its login confirmation and its
 * renewal — for two reasons that no call site could solve on its own.
 *
 * ORDERING. On `auth/user-token-expired` the SDK signs the user out locally from inside
 * `getIdToken` (`_logoutIfInvalidated`) and only THEN rejects, so by the time the rejection
 * is delivered the provider has already run its no-user branch and navigated. Classifying
 * here — around the read, before the rejection is folded into `SessionMovedError` or
 * swallowed by a `resolve(undefined)` — is what keeps the reason attached to the incident
 * that caused it instead of to a null callback that cannot know why it fired.
 *
 * OWNERSHIP. The scope is captured BEFORE the await and re-checked after it, so a read
 * that was started by an identity the tab has since replaced — a newer login, a tenant
 * switch, a logout — publishes nothing. `auth_time` cannot do this: it has second
 * precision, and a tenant switch moves no Firebase identity at all.
 *
 * The error is rethrown exactly as it arrived: every caller above this one still has to be
 * able to recognise `SessionMovedError`, a typed refusal or a connectivity fault.
 */
export async function captureSignOutReasonFromTokenRead<T>(
  read: () => Promise<T>,
): Promise<T> {
  const owner = scope;
  try {
    return await read();
  } catch (error) {
    if (owner === scope) {
      const reason = getSignOutReason(error);
      if (reason) noteSignOutReason(reason);
    }
    throw error;
  }
}
